import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, CircularProgress } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroller';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import {
  getDefaultProductFilters,
  ProductOrder,
} from '../../interfaces/Product/GetProductsRequest';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAnalytics } from '../../analytics/useAnalytics';
import { OfferSummary } from '../../interfaces/Offer/OfferSummary';
import { getProductsByOffer } from '../../api/Product';
import { getOffer } from '../../api/Offer';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { BreadcrumbElement } from '../../interfaces/Breadcrumb/BreadcrumbElement';
import { GenericBreadcrumbs } from '../../components/Breadcrumbs/GenericBreadcrumbs';
import { SortProductsHeader } from '../../components/SortProductsHeader/SortProductsHeader';
import { ProductGrid } from '../../components/ProductGrid/ProductGrid';
import { OfferCard } from '../../components/Offer/OfferCard';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export const OfferProductsPage = () => {
  const t = useLocalizedText();

  const { viewContent } = useAnalytics();

  const { offerID: offerISAsStr } = useParams<string>();

  const offerID = offerISAsStr ? Number(offerISAsStr) : -1;

  const [offer, setOffer] = useState<OfferSummary | undefined>(undefined);
  const [products, setProducts] = useState<PaginatedResponse<ProductSummary>>({
    data: [],
    totalCount: 0,
  });
  const [order, setOrder] = useState<ProductOrder>('orderByQuantityDesc');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    setLoading(true);
    getProductsByOffer(offerID, {
      ...getDefaultProductFilters(),
      order,
      page: 0,
    })
      .then(setProducts)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));

    getOffer(offerID)
      .then(setOffer)
      .catch((err) => toast.error(err.message));

    setPage(0);

    viewContent({ contentType: 'product_group', slug: `offer-${offerID}` });
  }, [offerID]);

  const incrementPage = useCallback(() => {
    if (loading || products.data.length >= products.totalCount) return;

    const nextPage = page + 1;
    setLoading(true);

    getProductsByOffer(offerID, {
      ...getDefaultProductFilters(),
      order,
      page: nextPage,
    })
      .then((nextProducts) => {
        setProducts((prev) => ({
          data: [...prev.data, ...nextProducts.data],
          totalCount: nextProducts.totalCount,
        }));
        setPage(nextPage);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [loading, page, offerID, products]);

  const handleSort = (newOrder: ProductOrder) => {
    setOrder(newOrder);
    setPage(0);
    setLoading(true);
    getProductsByOffer(offerID, {
      ...getDefaultProductFilters(),
      order: newOrder,
      page: 0,
    })
      .then(setProducts)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const breadcrumbElements: BreadcrumbElement[] = [
    {
      name: offer?.name ?? t('offer_products.offered_products'),
      url: `kampanya/${offerID}`,
    },
  ];

  const resolvedLoading = loading && !products.data.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: 5,
      }}
    >
      <GenericHelmet
        title={t('offer_products.offered_products')}
        meta={
          offer
            ? { title: offer.name, description: offer.description }
            : undefined
        }
        breadcrumb={breadcrumbElements}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
          boxSizing: 'border-box',
          px: isMobile ? 1.5 : 5,
          py: 2.5,
        }}
      >
        <GenericBreadcrumbs breadcrumbElements={breadcrumbElements} />

        {!!offer && <OfferCard offer={offer} />}

        <SortProductsHeader
          loading={resolvedLoading}
          totalCount={products.totalCount}
          order={order}
          onChangeOrder={handleSort}
        />

        <InfiniteScroll
          pageStart={0}
          loadMore={incrementPage}
          hasMore={products.totalCount > products.data.length}
          loader={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <CircularProgress color='inherit' />
            </Box>
          }
          style={{ width: '100%' }}
        >
          <ProductGrid products={products.data} loading={resolvedLoading} />
        </InfiniteScroll>
      </Box>
    </Box>
  );
};
